import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';

const API = 'https://api.cpms-srlkevcharge.com';

const IconEdit = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);
const IconTrash = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);
const IconTag = () => (
  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M20.59 13.41L11 3.82V3H3v8l.82.82L13.41 20.6a2 2 0 002.83 0l4.34-4.34a2 2 0 000-2.83z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const IconSort = ({ direction }) => (
  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ marginLeft: 4, opacity: direction ? 1 : 0.3 }}>
    {direction === 'desc'
      ? <polyline points="6 9 12 15 18 9" />
      : <polyline points="18 15 12 9 6 15" />}
  </svg>
);

function StatusPill({ status }) {
  const s = (status || 'inactive').toLowerCase();
  return <span className={`tariff-status-pill tariff-status-${s === 'active' ? 'active' : 'inactive'}`}>{status}</span>;
}

function SortableHeader({ label, sortKey, currentSort, onSort }) {
  const isActive = currentSort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}
      <IconSort direction={isActive ? currentSort.direction : null} />
    </th>
  );
}

export default function ViewRFIDCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [billingFilter, setBillingFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'date_registered', direction: 'desc' });
  const navigate = useNavigate();
  const { showToast } = useAppContext();

  const fetchCards = async () => {
    try {
      const res = await axios.get(`${API}/cards/`);
      setCards(res.data);
    } catch (e) {
      console.error('Failed to fetch cards', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCards(); }, []);

  const stats = {
    total: cards.length,
    active: cards.filter(c => c.status === 'Active').length,
    inactive: cards.filter(c => c.status === 'Inactive').length,
    free: cards.filter(c => c.free_charging === 'Yes').length,
  };

  const getBillingLabel = (card) => {
    if (card.free_charging === 'Yes') return 'Free Charging';
    if (card.tariff_schedules && card.tariff_schedules.length > 0) return 'Assigned Tariff';
    return 'Station Default';
  };

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const filtered = cards.filter(c => {
    const matchesSearch = !filter ||
      c.uid.toLowerCase().includes(filter.toLowerCase()) ||
      c.organisation_name?.toLowerCase().includes(filter.toLowerCase()) ||
      c.rfid_type?.toLowerCase().includes(filter.toLowerCase());

    const matchesBilling = billingFilter === 'all' ||
      (billingFilter === 'free' && c.free_charging === 'Yes') ||
      (billingFilter === 'tariff' && c.free_charging !== 'Yes' && c.tariff_schedules?.length > 0) ||
      (billingFilter === 'default' && c.free_charging !== 'Yes' && (!c.tariff_schedules || c.tariff_schedules.length === 0));

    return matchesSearch && matchesBilling;
  });

  const sorted = [...filtered].sort((a, b) => {
    let valA, valB;
    switch (sort.key) {
      case 'uid': valA = a.uid; valB = b.uid; break;
      case 'organisation_name': valA = a.organisation_name || ''; valB = b.organisation_name || ''; break;
      case 'rfid_type': valA = a.rfid_type || ''; valB = b.rfid_type || ''; break;
      case 'status': valA = a.status; valB = b.status; break;
      case 'date_registered': valA = new Date(a.date_registered); valB = new Date(b.date_registered); break;
      default: valA = a[sort.key]; valB = b[sort.key];
    }
    if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleToggleStatus = async (card) => {
    const newStatus = card.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await axios.patch(`${API}/cards/${card.id}/status?status=${newStatus}`);
      showToast(`Card ${card.uid} set to ${newStatus}`);
      fetchCards();
    } catch {
      showToast('Failed to update card status', 'error');
    }
  };

  const handleDelete = async (card) => {
    if (!window.confirm(`Remove card ${card.uid}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/cards/${card.id}`);
      showToast(`Card ${card.uid} removed`);
      fetchCards();
    } catch {
      showToast('Failed to remove card', 'error');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d + 'Z').toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  };

  if (loading) return <div className="loading">Loading cards...</div>;

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1>RFID Cards</h1>
          <p>All registered cards and their assigned billing across the network</p>
        </div>
        <button className="btn btn-green" onClick={() => navigate('/rfid-cards/add')}>
          + Add RFID Card
        </button>
      </div>

      <div className="stat-bar">
        <div className="card stat-card">
          <div className="stat-label">Total Cards</div>
          <div className="stat-number total">{stats.total}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-number available">{stats.active}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Inactive</div>
          <div className="stat-number" style={{ color: 'var(--slate)' }}>{stats.inactive}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Free Charging</div>
          <div className="stat-number" style={{ color: 'var(--amber)' }}>{stats.free}</div>
        </div>
      </div>

      <div className="card">
        <div className="section-header">
          <h2>Card List</h2>
        </div>
        <div style={{ padding: '0 20px 16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="filter-input"
            placeholder="Search by UID, organisation, or type..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ width: 300 }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { key: 'all', label: 'All' },
              { key: 'tariff', label: 'Assigned Tariff' },
              { key: 'default', label: 'Station Default' },
              { key: 'free', label: 'Free Charging' },
            ].map(opt => (
              <button
                key={opt.key}
                className={`btn ${billingFilter === opt.key ? 'btn-green' : 'btn-ghost'}`}
                style={{ padding: '6px 14px', fontSize: 12.5 }}
                onClick={() => setBillingFilter(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="table-wrap">
          {sorted.length === 0 ? (
            <div className="empty-state">
              <h3>{cards.length === 0 ? 'No cards registered yet' : 'No results'}</h3>
              <p>
                {cards.length === 0
                  ? 'Register your first RFID card to get started.'
                  : 'Try a different search term or filter.'}
              </p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <SortableHeader label="Card UID" sortKey="uid" currentSort={sort} onSort={handleSort} />
                  <SortableHeader label="Organisation" sortKey="organisation_name" currentSort={sort} onSort={handleSort} />
                  <SortableHeader label="Type" sortKey="rfid_type" currentSort={sort} onSort={handleSort} />
                  <th>Billing</th>
                  <SortableHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} />
                  <SortableHeader label="Date Registered" sortKey="date_registered" currentSort={sort} onSort={handleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(card => (
                  <tr key={card.id}>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600 }}>
                      {card.uid}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{card.organisation_name || '—'}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{card.rfid_type || '—'}</td>
                    <td>
                      {card.free_charging === 'Yes' ? (
                        <span className="tariff-tag">Free Charging</span>
                      ) : card.tariff_schedules && card.tariff_schedules.length > 0 ? (
                        card.tariff_schedules.map((s, i) => (
                          <span className="tariff-tag" key={i}><IconTag /> {s.tariff_name}</span>
                        ))
                      ) : (
                        <span className="tariff-tag none">Station default</span>
                      )}
                    </td>
                    <td><StatusPill status={card.status} /></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{formatDate(card.date_registered)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className={`btn ${card.status === 'Active' ? 'btn-ghost' : 'btn-success'}`}
                          style={{ padding: '5px 12px', fontSize: 12 }}
                          onClick={() => handleToggleStatus(card)}
                        >
                          {card.status === 'Active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '5px 12px', fontSize: 12 }}
                          onClick={() => handleDelete(card)}
                        >
                          <IconTrash /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}